import './BlendInProgressHint.css';

function formatList(names) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Premium placeholder shown in the "Your Custom Blend" slot while the
 * customer is still adjusting their ratios in user_ratios mode and the
 * blend isn't yet ready. Three failure modes are surfaced with friendly
 * copy:
 *   1. Sum < 100 → "add X% more"
 *   2. Sum > 100 → "reduce by X%"
 *   3. Sum = 100 but some ingredients are at 0% → ask to either remove
 *      the zero-valued ingredients or give them a percentage.
 */
function BlendInProgressHint({ total, zeroIngredientNames = [] }) {
  const safeTotal = Number.isFinite(total) ? total : 0;
  const ringPct = Math.max(0, Math.min(100, safeTotal));
  const display = `${(Math.round(safeTotal * 10) / 10).toString()}%`;

  const isAtHundred = Math.abs(safeTotal - 100) <= 1e-9;
  const hasZeros = Array.isArray(zeroIngredientNames) && zeroIngredientNames.length > 0;

  let title = 'Your blend, in progress';
  let body;

  if (hasZeros && isAtHundred) {
    // Sum is 100 but at least one ingredient is at 0%. The customer
    // probably forgot to give it a value or doesn't actually want it.
    const list = formatList(zeroIngredientNames);
    const isPlural = zeroIngredientNames.length > 1;
    title = isPlural
      ? 'A few ingredients still need a percentage'
      : 'One ingredient still needs a percentage';
    body = (
      <p className="blend-in-progress__body">
        <span className="blend-in-progress__delta">{list}</span>{' '}
        {isPlural ? 'are' : 'is'} set to 0%. Give{' '}
        {isPlural ? 'them' : 'it'} a percentage, or remove{' '}
        {isPlural ? 'them' : 'it'} from the blend.
      </p>
    );
  } else if (safeTotal < 100) {
    const delta = Math.round((100 - safeTotal) * 10) / 10;
    body = (
      <p className="blend-in-progress__body">
        Add{' '}
        <span className="blend-in-progress__delta">{delta}%</span>{' '}
        more across your ingredients to complete the blend.
      </p>
    );
  } else if (safeTotal > 100) {
    const delta = Math.round((safeTotal - 100) * 10) / 10;
    body = (
      <p className="blend-in-progress__body">
        Reduce by{' '}
        <span className="blend-in-progress__delta">{delta}%</span>{' '}
        across your ingredients to land on 100%.
      </p>
    );
  } else {
    body = (
      <p className="blend-in-progress__body">
        Almost — finalising your blend.
      </p>
    );
  }

  return (
    <section
      className="blend-in-progress"
      aria-label="Blend in progress"
      aria-live="polite"
    >
      <div
        className="blend-in-progress__ring"
        style={{ '--ring-pct': ringPct }}
        aria-hidden="true"
      >
        <span className="blend-in-progress__ring-inner">{display}</span>
      </div>
      <h2 className="blend-in-progress__title">{title}</h2>
      {body}
    </section>
  );
}

export default BlendInProgressHint;
